import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { X, Download, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

interface AdminImageZoomProps {
  src: string;
  alt: string;
  file: {
    name: string;
    created_at: string;
    updated_at: string;
    username?: string;
    conversationId?: string;
    directory?: string;
  };
  userProfiles: Record<string, string>;
  onDownload: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  children: React.ReactNode;
}

export const AdminImageZoom: React.FC<AdminImageZoomProps> = ({ 
  src, 
  alt, 
  file, 
  userProfiles, 
  onDownload, 
  onDelete, 
  isDeleting, 
  children 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const preventContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const preventDragStart = (e: React.DragEvent) => {
    e.preventDefault();
    return false;
  };

  const getDisplayUsername = () => {
    if (!file.username) return '';
    return userProfiles[file.username] || file.username.substring(0, 8) + '...';
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent 
        className={`max-w-[100vw] max-h-[100vh] w-full h-full bg-black/95 border-none ${isMobile ? 'p-0' : 'p-4'}`}
      >
        <DialogDescription className="sr-only">
          Visualize e gerencie a imagem {file.name}
        </DialogDescription>
        {/* Close button */}
          <div className="absolute top-4 right-4 z-10">
            <Button
              onClick={() => setIsOpen(false)}
              size="sm"
              variant="secondary"
              className="bg-black/50 hover:bg-black/70 text-white border-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Image container */}
          <div className="flex-1 flex items-center justify-center w-full h-full p-4 pb-32">
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full object-contain select-none"
              onContextMenu={preventContextMenu}
              onDragStart={preventDragStart}
              draggable={false}
              style={{ 
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
            />
          </div>

          {/* Details panel at bottom */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-4 md:p-6">
            <div className="max-w-4xl mx-auto space-y-4">
              {/* File info */}
              <div className="space-y-2">
                <h3 className="text-white font-medium text-lg truncate">{file.name}</h3>
                <div className="flex flex-wrap gap-4 text-sm text-white/70">
                  {getDisplayUsername() && (
                    <div>
                      <span className="text-luxury-gold">Usuário:</span> @{getDisplayUsername()}
                    </div>
                  )}
                  {file.conversationId && (
                    <div>
                      <span className="text-blue-400">Conversa:</span> {file.conversationId.substring(0, 8)}
                    </div>
                  )}
                  {file.directory && (
                    <div>
                      <span className="text-white/60">Diretório:</span> {file.directory}
                    </div>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="flex flex-wrap gap-4 text-sm text-white/60">
                <div>
                  <span className="text-white/80">Criado:</span> {new Date(file.created_at).toLocaleDateString('pt-BR')}
                </div>
                <div>
                  <span className="text-white/80">Atualizado:</span> {new Date(file.updated_at).toLocaleDateString('pt-BR')}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={handleDownload}
                  size="sm"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  size="sm"
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/30"
                >
                  {isDeleting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400 mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Excluir
                </Button>
              </div>
            </div>
          </div>
      </DialogContent>
    </Dialog>
  );
};
